import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Linkedin } from 'lucide-react';

// Shared footer used on both the landing page and the company subpage.
// `landing` links point to landing sections, `company` links to company-subpage sections,
// `external` opens a URL. Links scroll when the target is on the current page, else navigate.
const columns = [
  {
    title: 'Products',
    links: [
      { label: 'Deal OS™', landing: 'deal-aos' },
      { label: 'Workflows', landing: 'innovations' },
      { label: 'Brain', landing: 'deal-aos' },
      { label: 'Studio', landing: 'innovations' },
      { label: 'Memo', landing: 'innovations' },
      { label: 'Model', landing: 'innovations' },
      { label: 'Chat', landing: 'deal-aos' },
    ],
  },
  {
    title: 'Lifecycle',
    links: [
      { label: 'Sourcing', landing: 'lifecycle' },
      { label: 'Diligence', landing: 'lifecycle' },
      { label: 'Value Creation', landing: 'lifecycle' },
      { label: 'Strategic Exit', landing: 'lifecycle' },
    ],
    cta: { text: 'Live Mandates', icon: true },
  },
  {
    title: 'Practices',
    links: [
      { label: 'Venture Capital', landing: 'practices', pillar: 0 },
      { label: 'Private Equity', landing: 'practices', pillar: 1 },
      { label: 'Corporate M&A', landing: 'practices', pillar: 2 },
      { label: 'Strategy Advisory', landing: 'practices', pillar: 3 },
      { label: 'Investment Banking', landing: 'practices', pillar: 4 },
    ],
  },
  {
    title: 'Sovereignty',
    links: [
      { label: 'Zero MNPI Policy', landing: 'sovereignty' },
      { label: 'In-Tenant Perimeter', landing: 'sovereignty' },
      { label: 'Off-LLM Math', landing: 'sovereignty' },
      { label: 'Cell-Level Lineage', landing: 'sovereignty' },
      { label: 'SOC 2 & Compliance', landing: 'sovereignty' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', company: 'about' },
      { label: 'Leadership', company: 'leadership' },
      { label: 'News', company: 'systems' },
      { label: 'Footprint', company: 'footprint' },
    ],
    cta: { text: 'Contact', arrow: true, amber: true },
  },
];

export const Footer = ({ onOpenBriefing }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const goToLink = (link) => {
    if (link.external) {
      window.open(link.external, '_blank', 'noopener,noreferrer');
      return;
    }
    if (link.company) {
      // Company-subpage section.
      if (location.pathname === '/company') {
        scrollToId(link.company);
      } else {
        navigate(`/company#${link.company}`);
      }
      return;
    }
    // Landing section.
    if (location.pathname === '/') {
      scrollToId(link.landing);
      if (typeof link.pillar === 'number') {
        window.dispatchEvent(new CustomEvent('frontier:activate-pillar', { detail: link.pillar }));
      }
    } else {
      navigate(`/#${link.landing}`);
      if (typeof link.pillar === 'number') {
        setTimeout(() => window.dispatchEvent(new CustomEvent('frontier:activate-pillar', { detail: link.pillar })), 600);
      }
    }
  };

  const handleCta = (text) => onOpenBriefing && onOpenBriefing(text === 'Live Mandates' ? 'Live Transaction Mandate' : 'Co-Design Partnership');

  return (
    <footer className="bg-[#FBFBFB] border-t border-slate-200 text-slate-800">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 pt-20">

        {/* Masthead: Brand anchor + 5 directory columns */}
        <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-14 pb-6">

          {/* Left: Brand monograph & institutional anchor */}
          <div className="lg:w-[28%] xl:w-[26%] shrink-0 lg:border-r lg:border-slate-200/80 lg:pr-10 xl:pr-12 pb-8 lg:pb-0 border-b lg:border-b-0 border-slate-200/80">
            <div className="space-y-4">
              <div>
                <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'auto' }); }} className="font-extrabold tracking-[0.28em] text-slate-900 text-lg uppercase block hover:opacity-80 transition">
                  FRONTIER
                </button>
                <div className="text-[10px] font-mono text-[#F58220] font-semibold uppercase tracking-widest mt-0.5">Deal OS™ · Operating System</div>
              </div>

              <p className="text-slate-600 text-xs leading-relaxed font-sans pr-2">
                The Sovereign AI Platform for Strategy &amp; High-Stakes Deals. Cognitive thesis underwriting and transaction execution built for investment committees, C-suites, and global strategy practices.
              </p>
            </div>
          </div>

          {/* Right: 5-column directory grid */}
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 lg:gap-8 xl:gap-10 lg:pl-2">
            {columns.map((col) => (
              <div key={col.title}>
                <h4 className="text-[10px] font-mono font-bold uppercase text-slate-900 tracking-wider mb-4">{col.title}</h4>
                <ul className="space-y-2.5 text-xs text-slate-600">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <button onClick={() => goToLink(link)} className="hover:text-[#F58220] transition text-left">{link.label}</button>
                    </li>
                  ))}
                  {col.cta && (
                    <li className="pt-1">
                      <button
                        onClick={() => handleCta(col.cta.text)}
                        className={`inline-flex items-center gap-1 font-medium transition ${col.cta.amber ? 'text-[#F58220] hover:text-slate-900' : 'text-slate-900 hover:text-[#F58220]'}`}
                      >
                        <span>{col.cta.text}</span>
                        {col.cta.icon && <ArrowUpRight size={12} />}
                        {col.cta.arrow && <ArrowRight size={12} />}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Watermark section with legal strip (same canvas as the masthead) */}
      <div className="bg-[#FBFBFB] relative overflow-hidden">
        {/* Giant transparent FRONTIER watermark */}
        <div className="relative flex items-start justify-center select-none pointer-events-none" style={{ height: 'clamp(120px, 22vw, 320px)', overflow: 'hidden' }}>
          <svg viewBox="0 0 1600 280" preserveAspectRatio="xMidYMin meet" className="w-full">
            <defs>
              <linearGradient id="frontier-glow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.9" />
                <stop offset="45%" stopColor="#dbe1ea" stopOpacity="0.6" />
                <stop offset="75%" stopColor="#e9edf3" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
            </defs>
            <text x="800" y="230" textAnchor="middle" fill="url(#frontier-glow)" fontFamily="'Inter', system-ui, -apple-system, sans-serif" fontWeight="900" fontSize="260" letterSpacing="0.08em">
              FRONTIER
            </text>
          </svg>
        </div>

        {/* Legal & regulatory bottom ledger */}
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 relative z-10 pb-10">
          <div className="border-t border-slate-200/80 pt-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 text-[11px] font-mono text-slate-500 leading-relaxed">
            <div className="max-w-2xl">
              <span className="text-slate-800 font-semibold block mb-0.5">Regulatory Disclosure &amp; Legal Entity</span>
              Frontier Alpha Artificial Intelligence Consultancy L.L.C - S.P.C is registered and licensed under the Abu Dhabi Department of Economic Development (Licence No. CN-6580462). Headquarters: Abu Dhabi, United Arab Emirates.
            </div>

            <div className="flex flex-wrap items-center gap-5 sm:gap-7 shrink-0 text-[11px]">
              <a href="https://www.linkedin.com/company/frontier-strategy-agents/" target="_blank" rel="noopener noreferrer" className="hover:text-[#F58220] transition flex items-center gap-1.5 text-slate-600 font-sans font-medium">
                <Linkedin size={14} className="text-slate-500" />
                <span>LinkedIn</span>
              </a>
              <span className="text-slate-500">© 2026 Frontier. All rights reserved.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
